/**
 * PricingPage — standalone /pricing route.
 *
 * Uses the same PricingSection component as the public landing page,
 * wrapped in the v2 glass nav shell.
 */

import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@d-m-organization/hub-shared-lib/auth";
import { LogoMark } from "../components/LogoMark.js";
import { PricingSection } from "../components/PricingSection.js";

// ── Design tokens v2 ──────────────────────────────────────────────────────
const C = {
  purple: "#351DBD",
  navy: "#261F59",
  muted: "#E3EBF6",
  bg: "#f0eef6",
  faint: "#8a88a3",
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// PRICING PAGE
// ═══════════════════════════════════════════════════════════════════════════

export function PricingPage() {
  const { isAuthed } = useAuth();

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      {/* ── Glass nav ────────────────────────────────────────────────── */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: `1px solid ${C.muted}`,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
          }}
        >
          <Link
            to="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              textDecoration: "none",
              color: C.navy,
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: "-0.02em",
            }}
          >
            <LogoMark color={C.purple} size={28} />
            <span>Gen Pro Hub</span>
          </Link>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {isAuthed ? (
              <Link to="/" className="brand-btn brand-btn-primary brand-btn-sm">
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="brand-btn brand-btn-ghost brand-btn-sm">
                  Sign in
                </Link>
                <Link to="/signup" className="brand-btn brand-btn-primary brand-btn-sm">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── Pricing content (same component as landing page) ─────── */}
      <PricingSection />

      {/* ── Footer bar (legal links) ─────────────────────────────── */}
      <footer style={{ borderTop: `1px solid ${C.muted}`, padding: "28px 0" }}>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
            fontSize: 13,
          }}
        >
          <span style={{ color: C.faint }}>© {new Date().getFullYear()} Gen Pro Hub</span>
          <span style={{ display: "inline-flex", gap: 20 }}>
            <Link to="/terms" style={{ color: C.purple, textDecoration: "none", fontWeight: 600 }}>
              Terms of Service
            </Link>
            <Link to="/privacy" style={{ color: C.purple, textDecoration: "none", fontWeight: 600 }}>
              Privacy Policy
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
